import React, { useEffect, useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/upr-api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, LogOut, Save, Send, Star } from "lucide-react";
import FileUpload from "@/components/FileUpload";
import ImportAdmin from "@/pages/admin/ImportAdmin";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";
import AuditAdmin from "@/pages/admin/AuditAdmin";
import TagInput from "@/components/TagInput";
import EmailTemplatesAdmin from "@/pages/admin/EmailTemplatesAdmin";
import ShippingAdmin from "@/pages/admin/ShippingAdmin";
import PlaylistsAdmin from "@/pages/admin/PlaylistsAdmin";
import RichTextEditor from "@/components/RichTextEditor";

const TABS = ["releases", "artists", "team", "merch", "videos", "blog", "playlists", "import", "orders", "audit", "codes", "subscribers", "messages", "emails", "shipping", "settings"];

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const t = searchParams.get("tab");
    return t && TABS.includes(t) ? t : "releases";
  });

  // Keep the active tab in sync with the URL so links like the Audit tab's
  // "Fix" button (?tab=releases&edit=<id>) actually land on the right tab.
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && TABS.includes(t) && t !== tab) setTab(t);
  }, [searchParams]);

  const editId = searchParams.get("edit");
  const clearEditParam = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
  };

  if (loading) return <div className="px-6 py-24" data-testid="admin-loading">Checking session…</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="px-6 md:px-12 lg:px-16 py-10" data-testid="admin-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="upr-eyebrow mb-2">Admin Dashboard</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl md:text-5xl" style={{ letterSpacing: "-0.03em" }}>
            Universo Positivo — Control Room
          </h1>
          <p className="text-sm mt-2" style={{ color: "var(--upr-muted)" }}>
            Signed in as <strong>{user.email}</strong>
          </p>
        </div>
        <div className="flex gap-3">
          <Link to="/" className="upr-btn upr-btn-ghost" data-testid="view-site">View site</Link>
          <button onClick={logout} className="upr-btn upr-btn-ghost" data-testid="logout-btn">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      <nav className="mt-10 flex flex-wrap gap-2 border-b" style={{ borderColor: "var(--upr-border)" }} data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`tab-${t}`}
            className={
              "px-4 py-2 font-heading uppercase tracking-widest text-xs border-b-2 -mb-px transition-colors " +
              (tab === t ? "border-[color:var(--upr-primary)] text-[color:var(--upr-primary)]" : "border-transparent hover:text-[color:var(--upr-primary)]")
            }
          >
            {t}
          </button>
        ))}
      </nav>

      <div className="mt-10">
        {tab === "releases" && <ReleasesAdmin openEditId={editId} onOpenedEdit={clearEditParam} />}
        {tab === "artists" && <ArtistsAdmin />}
        {tab === "team" && <TeamAdmin />}
        {tab === "merch" && <MerchAdmin />}
        {tab === "videos" && <VideosAdmin />}
        {tab === "blog" && <BlogAdmin />}
        {tab === "playlists" && <PlaylistsAdmin />}
        {tab === "import" && <ImportAdmin />}
        {tab === "orders" && <OrdersAdmin />}
        {tab === "audit" && <AuditAdmin />}
        {tab === "codes" && <DiscountsAdmin />}
        {tab === "subscribers" && <SubscribersAdmin />}
        {tab === "messages" && <MessagesAdmin />}
        {tab === "emails" && <EmailTemplatesAdmin />}
        {tab === "shipping" && <ShippingAdmin />}
        {tab === "settings" && <SettingsAdmin />}
      </div>
    </div>
  );
}

/* --------------- shared editor shell --------------- */
function Section({ title, children, onAdd }) {
  return (
    <section>
      <div className="flex items-baseline justify-between mb-6">
        <h2 className="font-heading font-extrabold text-2xl tracking-tight">{title}</h2>
        {onAdd && (
          <button className="upr-btn" onClick={onAdd} data-testid={`add-${title.toLowerCase()}`}>
            <Plus size={14} /> New
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function Input({ label, value, onChange, type = "text", testid, required }) {
  return (
    <label className="block">
      <span className="upr-eyebrow block mb-2">{label}</span>
      <input
        type={type} value={value ?? ""} required={required}
        onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
        data-testid={testid}
        className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
        style={{ borderColor: "var(--upr-border)" }}
      />
    </label>
  );
}

function Textarea({ label, value, onChange, rows = 4, testid }) {
  return (
    <label className="block">
      <span className="upr-eyebrow block mb-2">{label}</span>
      <textarea
        value={value ?? ""} rows={rows} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
        style={{ borderColor: "var(--upr-border)" }}
      />
    </label>
  );
}

function Checkbox({ label, value, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      <span className="upr-eyebrow">{label}</span>
    </label>
  );
}

function Modal({ open, onClose, title, children, footer }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4" style={{ background: "rgba(28,27,26,0.55)" }} data-testid="admin-modal">
      <div className="relative max-w-3xl w-full mt-12 mb-12" style={{ background: "var(--upr-bg)", border: "1px solid var(--upr-border)" }}>
        <button className="absolute top-3 right-3 p-1 z-10" onClick={onClose} data-testid="modal-close"><X size={18} /></button>
        <div className="p-6 md:p-8 pb-4">
          <h3 className="font-heading font-extrabold text-2xl mb-6">{title}</h3>
          {children}
        </div>
        {footer && (
          <div
            className="sticky bottom-0 left-0 right-0 px-6 md:px-8 py-3 flex justify-end gap-2 backdrop-blur"
            style={{ background: "rgba(250,249,246,0.96)", borderTop: "1px solid var(--upr-border)" }}
            data-testid="modal-footer"
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

function ItemRow({ thumb, title, subtitle, onEdit, onDelete, testid }) {
  return (
    <div className="flex items-center gap-4 py-3 border-b" style={{ borderColor: "var(--upr-border)" }} data-testid={testid}>
      {thumb && <div className="w-14 h-14 shrink-0 overflow-hidden" style={{ border: "1px solid var(--upr-border)" }}><img src={thumb} alt="" className="w-full h-full object-cover" /></div>}
      <div className="flex-1 min-w-0">
        <div className="font-heading font-extrabold truncate">{title}</div>
        <div className="text-sm truncate" style={{ color: "var(--upr-muted)" }}>{subtitle}</div>
      </div>
      <div className="flex gap-2">
        <button className="upr-btn upr-btn-ghost" onClick={onEdit} data-testid={`${testid}-edit`}><Edit3 size={12} /> Edit</button>
        <button className="upr-btn upr-btn-ghost" onClick={onDelete} data-testid={`${testid}-delete`}><Trash2 size={12} /> Delete</button>
      </div>
    </div>
  );
}

/* --------------- RELEASES --------------- */
const EMPTY_RELEASE = { title: "", version: "", artist_id: "", artist_name: "", year: new Date().getFullYear(), catalog_number: "", format: "", cover_url: "", description: "", tracklist: [], tracks: [], credits: "", embed_url: "", buy_url: "", release_date: "", upc: "", tags: [], featured: false };

function ReleasesAdmin({ openEditId, onOpenedEdit } = {}) {
  const [items, setItems] = useState([]);
  const [artists, setArtists] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/releases").then(({ data }) => setItems(data));
  useEffect(() => { load(); api.get("/artists").then(({ data }) => setArtists(data)); }, []);

  const onAdd = () => { setEditing({ ...EMPTY_RELEASE, tracks: [], tags: [] }); setOpen(true); };
  const onEdit = (it) => { setEditing({ ...it, tracklist: it.tracklist || [], tracks: it.tracks || [], tags: it.tags || [] }); setOpen(true); };

  // Deep-link support: opens a specific release's editor when arriving here
  // with ?edit=<id>, e.g. from the Audit tab's "Fix" button.
  useEffect(() => {
    if (!openEditId || items.length === 0) return;
    const match = items.find((it) => it.id === openEditId);
    if (match) onEdit(match);
    onOpenedEdit?.();
  }, [openEditId, items]);
  const onDelete = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    try { await api.delete(`/releases/${it.id}`); toast.success("Release deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    try {
      const payload = { ...editing, year: Number(editing.year) || null, tracklist: editing.tracklist.filter(Boolean), tracks: (editing.tracks || []).filter((t) => t && (t.title || t.audio_url)), tags: (editing.tags || []).map((t) => String(t).trim()).filter(Boolean) };
      if (editing.id) await api.put(`/releases/${editing.id}`, payload);
      else await api.post("/releases", payload);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleFeatured = async (r) => {
    const next = !r.featured;
    try {
      if (next) {
        // Unfeature any currently featured releases first (UI single-featured invariant)
        const featuredOthers = items.filter((x) => x.featured && x.id !== r.id);
        await Promise.all(featuredOthers.map((x) => api.put(`/releases/${x.id}`, { ...x, featured: false })));
      }
      await api.put(`/releases/${r.id}`, { ...r, featured: next });
      toast.success(next ? `“${r.title}” is now featured` : `“${r.title}” unfeatured`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update featured release");
    }
  };

  return (
    <Section title="Releases" onAdd={onAdd}>
      <div className="mb-4 p-3 text-xs flex items-start gap-2" style={{ background: "var(--upr-surface)", border: "1px dashed var(--upr-border)", color: "var(--upr-muted)" }}>
        <Star size={14} className="shrink-0 mt-0.5" style={{ color: "var(--upr-primary)" }} fill="currentColor" />
        <span>Tap the <strong>star</strong> on any row to set it as the <strong>Featured release</strong> on the public Music page. Only one can be featured at a time.</span>
      </div>
      {items.length === 0 && <div className="text-sm" style={{ color: "var(--upr-muted)" }}>No releases yet.</div>}
      <div data-testid="admin-releases-list">
        {items.map((r) => (
          <div
            key={r.id}
            className="flex items-center gap-4 py-3 border-b"
            style={{
              borderColor: "var(--upr-border)",
              background: r.featured ? "rgba(217,90,59,0.06)" : "transparent",
            }}
            data-testid={`release-row-${r.id}`}
          >
            {r.cover_url && (
              <div className="w-14 h-14 shrink-0 overflow-hidden" style={{ border: "1px solid var(--upr-border)" }}>
                <img src={r.cover_url} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-heading font-extrabold truncate flex items-center gap-2">
                {r.title}
                {r.featured && (
                  <span
                    className="upr-eyebrow px-1.5 py-0.5"
                    style={{ background: "var(--upr-primary)", color: "var(--upr-bg)", fontSize: "0.55rem" }}
                    data-testid={`release-featured-badge-${r.id}`}
                  >
                    Featured
                  </span>
                )}
              </div>
              <div className="text-sm truncate" style={{ color: "var(--upr-muted)" }}>
                {r.artist_name} · {r.catalog_number || ""}{r.format ? " · " + r.format : ""}
              </div>
            </div>
            <button
              type="button"
              onClick={() => toggleFeatured(r)}
              className="upr-btn upr-btn-ghost"
              title={r.featured ? "Unfeature" : "Set as featured"}
              data-testid={`release-feature-${r.id}`}
            >
              <Star size={14} fill={r.featured ? "currentColor" : "none"} style={{ color: r.featured ? "var(--upr-primary)" : undefined }} />
              {r.featured ? "Featured" : "Feature"}
            </button>
            <button className="upr-btn upr-btn-ghost" onClick={() => onEdit(r)} data-testid={`release-row-${r.id}-edit`}><Edit3 size={12} /> Edit</button>
            <button className="upr-btn upr-btn-ghost" onClick={() => onDelete(r)} data-testid={`release-row-${r.id}-delete`}><Trash2 size={12} /> Delete</button>
          </div>
        ))}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing?.id ? "Edit release" : "New release"}
        footer={editing && (
          <>
            <button className="upr-btn upr-btn-ghost" onClick={() => setOpen(false)} type="button" data-testid="rel-cancel">Cancel</button>
            <button className="upr-btn" onClick={save} type="button" data-testid="rel-save"><Save size={14} /> Save release</button>
          </>
        )}
      >
        {editing && (
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="rel-title" required />
            <Input label="Version / Edition (e.g. 'Soul Hop Mix')" value={editing.version} onChange={(v) => setEditing({ ...editing, version: v })} testid="rel-version" />
            <label className="block">
              <span className="upr-eyebrow block mb-2">Artist</span>
              <select
                value={editing.artist_id || ""}
                onChange={(e) => {
                  const a = artists.find((x) => x.id === e.target.value);
                  setEditing({ ...editing, artist_id: e.target.value || "", artist_name: a ? a.name : editing.artist_name });
                }}
                data-testid="rel-artist"
                className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                style={{ borderColor: "var(--upr-border)" }}
              >
                <option value="">— Free text below —</option>
                {artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </label>
            <Input label="Artist name (display)" value={editing.artist_name} onChange={(v) => setEditing({ ...editing, artist_name: v })} testid="rel-artist-name" required />
            <Input label="Catalog number" value={editing.catalog_number} onChange={(v) => setEditing({ ...editing, catalog_number: v })} testid="rel-catalog" />
            <Input label="Format" value={editing.format} onChange={(v) => setEditing({ ...editing, format: v })} testid="rel-format" />
            <Input label="Year" type="number" value={editing.year} onChange={(v) => setEditing({ ...editing, year: v })} testid="rel-year" />
            <Input label="Release date (YYYY-MM-DD)" value={editing.release_date} onChange={(v) => setEditing({ ...editing, release_date: v })} testid="rel-date" />
            <Input label="UPC / Barcode" value={editing.upc} onChange={(v) => setEditing({ ...editing, upc: v })} testid="rel-upc" />
            <div className="md:col-span-2">
              <TagInput
                label="Tags / genres (type and press Enter or comma to add)"
                value={editing.tags || []}
                onChange={(tags) => setEditing({ ...editing, tags })}
                testid="rel-tags"
              />
            </div>
            <div className="md:col-span-2">
              <FileUpload label="Cover image" value={editing.cover_url} onChange={(v) => setEditing({ ...editing, cover_url: v })} kind="image" testid="rel-cover" />
            </div>
            <div className="md:col-span-2">
              <span className="upr-eyebrow block mb-2">Description</span>
              <RichTextEditor
                value={editing.description}
                onChange={(v) => setEditing({ ...editing, description: v })}
                testid="rel-desc"
                simple
                placeholder="Describe the release…"
              />
            </div>
            <div className="md:col-span-2">
              <Textarea label="Tracklist (one per line — display only)" value={(editing.tracklist || []).join("\n")} onChange={(v) => setEditing({ ...editing, tracklist: v.split("\n") })} testid="rel-tracklist" rows={6} />
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <span className="upr-eyebrow">Self-hosted tracks (audio player)</span>
                <button
                  type="button"
                  className="upr-btn upr-btn-ghost"
                  data-testid="rel-add-track"
                  onClick={() => setEditing({ ...editing, tracks: [...(editing.tracks || []), { title: "", audio_url: "" }] })}
                >
                  <Plus size={12} /> Add track
                </button>
              </div>
              {(editing.tracks || []).length === 0 && (
                <p className="text-xs" style={{ color: "var(--upr-muted)" }}>
                  No tracks. Upload MP3s here to show an inline audio player on the release page.
                </p>
              )}
              <div className="space-y-3">
                {(editing.tracks || []).map((t, i) => (
                  <div key={i} className="p-3 border" style={{ borderColor: "var(--upr-border)" }} data-testid={`rel-track-${i}`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 space-y-2">
                        <input
                          type="text"
                          placeholder={`Track ${i + 1} title`}
                          value={t.title || ""}
                          onChange={(e) => {
                            const tracks = [...editing.tracks];
                            tracks[i] = { ...tracks[i], title: e.target.value };
                            setEditing({ ...editing, tracks });
                          }}
                          data-testid={`rel-track-${i}-title`}
                          className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                          style={{ borderColor: "var(--upr-border)" }}
                        />
                        <FileUpload
                          label="Preview MP3 (public stream)"
                          value={t.preview_url || t.audio_url || ""}
                          kind="audio"
                          onChange={(v) => {
                            const tracks = [...editing.tracks];
                            tracks[i] = { ...tracks[i], preview_url: v, audio_url: v };
                            setEditing({ ...editing, tracks });
                          }}
                          testid={`rel-track-${i}-preview`}
                        />
                        <FileUpload
                          label="Download (MP3, customer-facing, gated)"
                          value={t.download_url || ""}
                          kind="audio"
                          onChange={(v) => {
                            const tracks = [...editing.tracks];
                            tracks[i] = { ...tracks[i], download_url: v };
                            setEditing({ ...editing, tracks });
                          }}
                          testid={`rel-track-${i}-download`}
                        />
                        <FileUpload
                          label="WAV master (archive — never delivered to customer)"
                          value={t.master_url || ""}
                          kind="audio"
                          onChange={(v) => {
                            const tracks = [...editing.tracks];
                            tracks[i] = { ...tracks[i], master_url: v };
                            setEditing({ ...editing, tracks });
                          }}
                          testid={`rel-track-${i}-master`}
                        />
                      </div>
                      <button
                        type="button"
                        className="upr-btn upr-btn-ghost"
                        data-testid={`rel-track-${i}-remove`}
                        onClick={() => setEditing({ ...editing, tracks: editing.tracks.filter((_, k) => k !== i) })}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="md:col-span-2">
                <span className="upr-eyebrow block mb-2">Credits</span>
                <RichTextEditor
                  value={editing.credits}
                  onChange={(v) => setEditing({ ...editing, credits: v })}
                  testid="rel-credits"
                  simple
                  placeholder="Production credits, personnel, etc."
                />
              </div>
            </div>
            <Input label="Embed URL (Bandcamp iframe / YouTube)" value={editing.embed_url} onChange={(v) => setEditing({ ...editing, embed_url: v })} testid="rel-embed" />
            <Input label="Buy URL" value={editing.buy_url} onChange={(v) => setEditing({ ...editing, buy_url: v })} testid="rel-buy" />
            <div className="md:col-span-2">
              <Checkbox label="Featured on Music page" value={editing.featured} onChange={(v) => setEditing({ ...editing, featured: v })} testid="rel-featured" />
            </div>
          </div>
        )}
      </Modal>
    </Section>
  );
}

/* --------------- ARTISTS --------------- */
const EMPTY_ARTIST = { name: "", photo_url: "", bio: "", website: "", instagram: "", bandcamp: "", display_order: 100 };
function ArtistsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/artists").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const onAdd = () => { setEditing({ ...EMPTY_ARTIST }); setOpen(true); };
  const onEdit = (it) => { setEditing({ ...it }); setOpen(true); };
  const onDelete = async (it) => {
    if (!window.confirm(`Delete artist "${it.name}"?`)) return;
    try { await api.delete(`/artists/${it.id}`); toast.success("Artist deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    try {
      if (editing.id) await api.put(`/artists/${editing.id}`, editing);
      else await api.post("/artists", editing);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Section title="Artists" onAdd={onAdd}>
      <div data-testid="admin-artists-list">
        {items.length === 0 && <div className="text-sm" style={{ color: "var(--upr-muted)" }}>No artists yet.</div>}
        {items.map((a) => (
          <ItemRow key={a.id} thumb={a.photo_url} title={a.name} subtitle={((a.bio || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 80))} onEdit={() => onEdit(a)} onDelete={() => onDelete(a)} testid={`artist-row-${a.id}`} />
        ))}
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing?.id ? "Edit artist" : "New artist"}
        footer={editing && (
          <>
            <button className="upr-btn upr-btn-ghost" onClick={() => setOpen(false)} type="button">Cancel</button>
            <button className="upr-btn" onClick={save} type="button" data-testid="art-save"><Save size={14} /> Save</button>
          </>
        )}
      >
        {editing && (
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="art-name" required />
            <div><FileUpload label="Photo" value={editing.photo_url} onChange={(v) => setEditing({ ...editing, photo_url: v })} kind="image" testid="art-photo" /></div>
            <div className="md:col-span-2">
              <span className="upr-eyebrow block mb-2">Bio</span>
              <RichTextEditor
                value={editing.bio}
                onChange={(v) => setEditing({ ...editing, bio: v })}
                testid="art-bio"
                simple
                placeholder="Tell the artist's story…"
              />
            </div>
            <Input label="Website" value={editing.website} onChange={(v) => setEditing({ ...editing, website: v })} testid="art-website" />
            <Input label="Bandcamp" value={editing.bandcamp} onChange={(v) => setEditing({ ...editing, bandcamp: v })} testid="art-bandcamp" />
            <Input label="Instagram" value={editing.instagram} onChange={(v) => setEditing({ ...editing, instagram: v })} testid="art-instagram" />
            <Input
              label="Display order (lower = shown first, e.g. 1)"
              type="number"
              value={editing.display_order ?? 100}
              onChange={(v) => setEditing({ ...editing, display_order: v === "" ? 100 : Number(v) })}
              testid="art-display-order"
            />
          </div>
        )}
      </Modal>
    </Section>
  );
}

/* --------------- TEAM --------------- */
const EMPTY_TEAM = { name: "", photo_url: "", bio: "", display_order: 100 };
function TeamAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/team").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const onAdd = () => { setEditing({ ...EMPTY_TEAM }); setOpen(true); };
  const onEdit = (it) => { setEditing({ ...it }); setOpen(true); };
  const onDelete = async (it) => {
    if (!window.confirm(`Delete team member "${it.name}"?`)) return;
    try { await api.delete(`/team/${it.id}`); toast.success("Team member deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const save = async () => {
    try {
      if (editing.id) await api.put(`/team/${editing.id}`, editing);
      else            await api.post("/team", editing);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Section title="Team" onAdd={onAdd}>
      <div data-testid="admin-team-list">
        {items.length === 0 && <div className="text-sm" style={{ color: "var(--upr-muted)" }}>No team members yet.</div>}
        {items.map((m) => (
          <ItemRow
            key={m.id}
            thumb={m.photo_url}
            title={m.name}
            subtitle={((m.bio || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 80))}
            onEdit={() => onEdit(m)}
            onDelete={() => onDelete(m)}
            testid={`team-row-${m.id}`}
          />
        ))}
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing?.id ? "Edit team member" : "New team member"}
        footer={editing && (
          <>
            <button className="upr-btn upr-btn-ghost" onClick={() => setOpen(false)} type="button">Cancel</button>
            <button className="upr-btn" onClick={save} type="button" data-testid="team-save"><Save size={14} /> Save</button>
          </>
        )}
      >
        {editing && (
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="team-name" required />
            <div><FileUpload label="Photo" value={editing.photo_url} onChange={(v) => setEditing({ ...editing, photo_url: v })} kind="image" testid="team-photo" /></div>
            <div className="md:col-span-2">
              <span className="upr-eyebrow block mb-2">Bio</span>
              <RichTextEditor
                value={editing.bio}
                onChange={(v) => setEditing({ ...editing, bio: v })}
                testid="team-bio"
                simple
                placeholder="Role, story, a line or two…"
              />
            </div>
            <Input
              label="Display order (lower = shown first, e.g. 1)"
              type="number"
              value={editing.display_order ?? 100}
              onChange={(v) => setEditing({ ...editing, display_order: v === "" ? 100 : Number(v) })}
              testid="team-display-order"
            />
          </div>
        )}
      </Modal>
    </Section>
  );
}

/* --------------- MERCH --------------- */
const EMPTY_MERCH = { name: "", category: "vinyl", description: "", price: 0, currency: "EUR", image_url: "", in_stock: true, buy_url: "", release_id: "", variants: [], stock: null, display_order: 100 };
function MerchAdmin() {
  const [items, setItems] = useState([]);
  const [releases, setReleases] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alertCounts, setAlertCounts] = useState({});  // {merchId: {pending, total}}

  const load = async () => {
    const [merchRes, relRes] = await Promise.all([api.get("/merch"), api.get("/releases")]);
    setItems(merchRes.data);
    setReleases(relRes.data || []);
    const counts = {};
    await Promise.all(merchRes.data.map(async (m) => {
      try {
        const { data: a } = await api.get(`/merch/${m.id}/alerts`);
        counts[m.id] = { pending: a.pending, total: a.total };
      } catch (e) {
        console.warn(`[admin] could not fetch alerts for merch ${m.id}:`, e?.response?.status);
      }
    }));
    setAlertCounts(counts);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        ...editing,
        price: Number(editing.price) || 0,
        display_order: Number(editing.display_order) || 100,
      };
      if (editing.id) await api.put(`/merch/${editing.id}`, payload);
      else await api.post("/merch", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onDelete = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    try { await api.delete(`/merch/${it.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendRestock = async (it) => {
    if (!it.in_stock) { toast.error("Flip the item in stock first."); return; }
    const counts = alertCounts[it.id];
    if (!counts || counts.pending === 0) { toast.info("No pending alerts."); return; }
    if (!window.confirm(`Send restock email to ${counts.pending} subscriber(s) for "${it.name}"?`)) return;
    try {
      const { data } = await api.post(`/merch/${it.id}/send-restock`);
      if (data.skipped) toast.info(data.reason || "Nothing to send.");
      else toast.success(`Restock email sent to ${data.sent} subscriber(s).`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Section title="Merch" onAdd={() => { setEditing({ ...EMPTY_MERCH }); setOpen(true); }}>
      <div data-testid="admin-merch-list">
        {items.length === 0 && <div className="text-sm" style={{ color: "var(--upr-muted)" }}>No merch yet.</div>}
        {items.map((m) => {
          const counts = alertCounts[m.id];
          const subtitleExtra = counts && counts.total > 0
            ? ` · 🔔 ${counts.pending} waiting / ${counts.total} total`
            : "";
          return (
            <div key={m.id} className="flex items-center gap-4 py-3 border-b" style={{ borderColor: "var(--upr-border)" }} data-testid={`merch-row-${m.id}`}>
              {m.image_url && <div className="w-14 h-14 shrink-0 overflow-hidden" style={{ border: "1px solid var(--upr-border)" }}><img src={m.image_url} alt="" className="w-full h-full object-cover" /></div>}
              <div className="flex-1 min-w-0">
                <div className="font-heading font-extrabold truncate">{m.name}</div>
                <div className="text-sm truncate" style={{ color: "var(--upr-muted)" }}>
                  {m.category} · {m.currency} {Number(m.price).toFixed(2)} · {m.in_stock ? "In stock" : "Sold out"}{subtitleExtra}
                </div>
              </div>
              <div className="flex gap-2">
                {counts && counts.pending > 0 && (
                  <button className="upr-btn" onClick={() => sendRestock(m)} data-testid={`merch-restock-${m.id}`} title={`Email ${counts.pending} subscribers`}>
                    <Send size={12} /> Restock {counts.pending}
                  </button>
                )}
                <button className="upr-btn upr-btn-ghost" onClick={() => { setEditing({ ...m }); setOpen(true); }} data-testid={`merch-row-${m.id}-edit`}><Edit3 size={12} /> Edit</button>
                <button className="upr-btn upr-btn-ghost" onClick={() => onDelete(m)} data-testid={`merch-row-${m.id}-delete`}><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          );
        })}
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing?.id ? "Edit merch" : "New merch"}
        footer={editing && (
          <>
            <button className="upr-btn upr-btn-ghost" onClick={() => setOpen(false)} type="button">Cancel</button>
            <button className="upr-btn" onClick={save} type="button" data-testid="mer-save"><Save size={14} /> Save</button>
          </>
        )}
      >
        {editing && (
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="mer-name" required />
            <label className="block">
              <span className="upr-eyebrow block mb-2">Category</span>
              <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                data-testid="mer-cat"
                className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]" style={{ borderColor: "var(--upr-border)" }}>
                <option value="vinyl">Vinyl</option>
                <option value="cd">CD</option>
                <option value="tshirt">T-Shirt</option>
                <option value="cap">Cap</option>
                <option value="digital">Digital (no shipping)</option>
                <option value="other">Other</option>
              </select>
            </label>
            <Input label="Price" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} testid="mer-price" />
            <Input label="Currency" value={editing.currency} onChange={(v) => setEditing({ ...editing, currency: v })} testid="mer-currency" />
            <div className="md:col-span-2"><FileUpload label="Image" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} kind="image" testid="mer-img" /></div>
            <div className="md:col-span-2"><Textarea label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testid="mer-desc" /></div>
            <Input label="Buy URL (optional, external)" value={editing.buy_url} onChange={(v) => setEditing({ ...editing, buy_url: v })} testid="mer-buy" />
            <Input
              label="Display order (lower = shown first, e.g. 1)"
              type="number"
              value={editing.display_order ?? 100}
              onChange={(v) => setEditing({ ...editing, display_order: v === "" ? 100 : Number(v) })}
              testid="mer-display-order"
            />
            <Checkbox label="In stock (used only when no variants & no stock # set)" value={editing.in_stock} onChange={(v) => setEditing({ ...editing, in_stock: v })} testid="mer-stock" />

            {/* MP3 bundle */}
            <label className="block md:col-span-2">
              <span className="upr-eyebrow block mb-2">Bundle MP3 download from release</span>
              <select
                value={editing.release_id || ""}
                onChange={(e) => setEditing({ ...editing, release_id: e.target.value })}
                data-testid="mer-release"
                className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                style={{ borderColor: "var(--upr-border)" }}
              >
                <option value="">— None (no digital download) —</option>
                {releases.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.cat_num || r.catalog_number || ""} · {r.title || "(untitled)"}
                  </option>
                ))}
              </select>
              <span className="block text-xs mt-1" style={{ color: "var(--upr-muted)" }}>
                Buyers of this physical item automatically receive download links for every track on the selected release that has a <strong>Download (MP3)</strong> file. Leave as &quot;None&quot; for non-music merch (caps, posters, etc.).
              </span>
            </label>

            {/* Variants (sizes/options) */}
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <span className="upr-eyebrow">Sizes / variants</span>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    className="upr-btn upr-btn-ghost"
                    onClick={() => {
                      const existing = new Set((editing.variants || []).map(v => (v.label || "").toUpperCase()));
                      const preset = ["S", "M", "L", "XL", "XXL"];
                      const additions = preset
                        .filter(lbl => !existing.has(lbl))
                        .map(lbl => ({ label: lbl, stock: 0 }));
                      if (additions.length === 0) return;
                      setEditing({ ...editing, variants: [...(editing.variants || []), ...additions] });
                    }}
                    data-testid="mer-add-clothing-sizes"
                    title="Adds S, M, L, XL, XXL rows with 0 stock — one item, multiple sizes."
                  >
                    <Plus size={12} /> Add clothing sizes (S/M/L/XL/XXL)
                  </button>
                  <button
                    type="button"
                    className="upr-btn upr-btn-ghost"
                    onClick={() => setEditing({ ...editing, variants: [...(editing.variants || []), { label: "", stock: 0 }] })}
                    data-testid="mer-add-variant"
                  >
                    <Plus size={12} /> Add custom size
                  </button>
                </div>
              </div>
              {(editing.variants || []).length === 0 ? (
                <div className="text-xs mb-2" style={{ color: "var(--upr-muted)" }}>
                  No variants — this item has a single SKU. For T-shirts and clothing, click <strong>&quot;Add clothing sizes&quot;</strong> above to enable per-size stock (S, M, L, XL, XXL) on this single item. Otherwise use the &quot;Total stock&quot; field below.
                </div>
              ) : (
                <div className="space-y-2">
                  {(editing.variants || []).map((v, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`mer-variant-${i}`}>
                      <input
                        type="text"
                        placeholder="Size / label (S, M, L, 180g Black…)"
                        value={v.label || ""}
                        onChange={(e) => {
                          const variants = [...editing.variants];
                          variants[i] = { ...variants[i], label: e.target.value };
                          setEditing({ ...editing, variants });
                        }}
                        data-testid={`mer-variant-${i}-label`}
                        className="flex-1 p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                        style={{ borderColor: "var(--upr-border)" }}
                      />
                      <input
                        type="number"
                        min="0"
                        placeholder="Qty"
                        value={v.stock ?? 0}
                        onChange={(e) => {
                          const variants = [...editing.variants];
                          variants[i] = { ...variants[i], stock: e.target.value };
                          setEditing({ ...editing, variants });
                        }}
                        data-testid={`mer-variant-${i}-stock`}
                        className="w-24 p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                        style={{ borderColor: "var(--upr-border)" }}
                      />
                      <button
                        type="button"
                        className="upr-btn upr-btn-ghost"
                        data-testid={`mer-variant-${i}-remove`}
                        onClick={() => setEditing({ ...editing, variants: editing.variants.filter((_, k) => k !== i) })}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Input
              label="Total stock (used only if no variants)"
              type="number"
              value={editing.stock ?? ""}
              onChange={(v) => setEditing({ ...editing, stock: v })}
              testid="mer-total-stock"
            />
          </div>
        )}
      </Modal>
    </Section>
  );
}

/* --------------- VIDEOS --------------- */
const EMPTY_VIDEO = { title: "", artist_name: "", youtube_url: "", description: "", release_id: "", display_order: 100 };
function VideosAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/videos").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...editing, display_order: Number(editing.display_order) || 100 };
      if (editing.id) await api.put(`/videos/${editing.id}`, payload);
      else await api.post("/videos", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onDelete = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    try { await api.delete(`/videos/${it.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Section title="Videos" onAdd={() => { setEditing({ ...EMPTY_VIDEO }); setOpen(true); }}>
      <div data-testid="admin-videos-list">
        {items.length === 0 && <div className="text-sm" style={{ color: "var(--upr-muted)" }}>No videos yet.</div>}
        {items.map((v) => (
          <ItemRow key={v.id} title={v.title} subtitle={`${v.artist_name || ""} · ${v.youtube_url}`} onEdit={() => { setEditing({ ...v }); setOpen(true); }} onDelete={() => onDelete(v)} testid={`vid-row-${v.id}`} />
        ))}
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing?.id ? "Edit video" : "New video"}
        footer={editing && (
          <>
            <button className="upr-btn upr-btn-ghost" onClick={() => setOpen(false)} type="button">Cancel</button>
            <button className="upr-btn" onClick={save} type="button" data-testid="vid-save"><Save size={14} /> Save</button>
          </>
        )}
      >
        {editing && (
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="vid-title" required />
            <Input label="Artist" value={editing.artist_name} onChange={(v) => setEditing({ ...editing, artist_name: v })} testid="vid-artist" />
            <div className="md:col-span-2"><Input label="YouTube URL (watch or embed)" value={editing.youtube_url} onChange={(v) => setEditing({ ...editing, youtube_url: v })} testid="vid-url" required /></div>
            <div className="md:col-span-2"><Textarea label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testid="vid-desc" /></div>
            <Input
              label="Display order (lower = shown first, e.g. 1)"
              type="number"
              value={editing.display_order ?? 100}
              onChange={(v) => setEditing({ ...editing, display_order: v === "" ? 100 : Number(v) })}
              testid="vid-display-order"
            />
          </div>
        )}
      </Modal>
    </Section>
  );
}

/* --------------- BLOG --------------- */
const EMPTY_POST = { title: "", slug: "", excerpt: "", body: "", cover_url: "", author: "Universo Positivo", published: true, display_order: 100 };

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function BlogAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/blog/admin").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        ...editing,
        slug: editing.slug || slugify(editing.title),
        display_order: Number(editing.display_order) || 100,
      };
      if (editing.id) await api.put(`/blog/${editing.id}`, payload);
      else await api.post("/blog", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onDelete = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    try { await api.delete(`/blog/${it.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Section title="Blog" onAdd={() => { setEditing({ ...EMPTY_POST }); setOpen(true); }}>
      <div data-testid="admin-blog-list">
        {items.length === 0 && <div className="text-sm" style={{ color: "var(--upr-muted)" }}>No posts yet.</div>}
        {items.map((p) => (
          <ItemRow key={p.id} thumb={p.cover_url} title={p.title} subtitle={`${p.published ? "Published" : "Draft"} · /${p.slug}`} onEdit={() => { setEditing({ ...p }); setOpen(true); }} onDelete={() => onDelete(p)} testid={`blog-row-${p.id}`} />
        ))}
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing?.id ? "Edit post" : "New post"}
        footer={editing && (
          <>
            <button className="upr-btn upr-btn-ghost" onClick={() => setOpen(false)} type="button">Cancel</button>
            <button className="upr-btn" onClick={save} type="button" data-testid="post-save"><Save size={14} /> Save</button>
          </>
        )}
      >
        {editing && (
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Title" value={editing.title}
              onChange={(v) => setEditing({ ...editing, title: v, slug: editing.id ? editing.slug : slugify(v) })}
              testid="post-title" required />
            <Input label="Slug" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} testid="post-slug" required />
            <div className="md:col-span-2"><FileUpload label="Cover image" value={editing.cover_url} onChange={(v) => setEditing({ ...editing, cover_url: v })} kind="image" testid="post-cover" /></div>
            <div className="md:col-span-2"><Textarea label="Excerpt" value={editing.excerpt} onChange={(v) => setEditing({ ...editing, excerpt: v })} testid="post-excerpt" rows={2} /></div>
            <div className="md:col-span-2">
              <span className="upr-eyebrow block mb-2">Body</span>
              <RichTextEditor value={editing.body} onChange={(v) => setEditing({ ...editing, body: v })} testid="post-body" placeholder="Start writing the post…" />
            </div>
            <Input label="Author" value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} testid="post-author" />
            <Input
              label="Display order (lower = shown first, e.g. 1)"
              type="number"
              value={editing.display_order ?? 100}
              onChange={(v) => setEditing({ ...editing, display_order: v === "" ? 100 : Number(v) })}
              testid="post-display-order"
            />
            <Checkbox label="Published" value={editing.published} onChange={(v) => setEditing({ ...editing, published: v })} testid="post-published" />
          </div>
        )}
      </Modal>
    </Section>
  );
}

/* --------------- ORDERS --------------- */
function OrdersAdmin() {
  const [items, setItems] = useState([]);
  const [resending, setResending] = useState(null);
  const [resendingConf, setResendingConf] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const load = () => api.get("/orders").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const resend = async (o) => {
    setResending(o.session_id);
    try {
      const { data } = await api.post(`/downloads/resend/${o.session_id}`);
      toast.success(`Resent download link to ${data.email}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not re-send download");
    } finally {
      setResending(null);
    }
  };

  const resendConfirmation = async (o) => {
    setResendingConf(o.session_id);
    try {
      const { data } = await api.post(`/orders/${o.session_id}/resend-confirmation`);
      toast.success(`Resent order confirmation to ${data.email}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not re-send confirmation");
    } finally {
      setResendingConf(null);
    }
  };

  const onDelete = async (o) => {
    // Show the order total in the prompt so it's obvious what's being removed.
    const label = `${(o.currency || "").toUpperCase()} ${Number(o.amount).toFixed(2)}${o.email ? ` — ${o.email}` : ""}`;
    if (!window.confirm(`Delete this order (${label})? This cannot be undone.`)) return;
    setDeleting(o.session_id);
    try {
      await api.delete(`/orders/${o.session_id}`);
      toast.success("Order deleted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete order");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <Section title="Orders (Stripe)">
      <div data-testid="admin-orders-list">
        {items.length === 0 && <div className="text-sm" style={{ color: "var(--upr-muted)" }}>No orders yet.</div>}
        {items.map((o) => {
          const lines = o.items && o.items.length > 0 ? o.items : [{ merch_name: o.merch_name, quantity: o.quantity, variant_label: o.variant_label }];
          return (
          <div key={o.id} className="border p-4 mb-3" style={{ borderColor: "var(--upr-border)" }} data-testid={`order-${o.id}`}>
            <div className="flex items-baseline justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-heading font-extrabold truncate">
                  {lines.map((l, i) => (
                    <span key={i}>
                      {i > 0 && <span style={{ color: "var(--upr-muted)" }}> · </span>}
                      {l.merch_name}{l.variant_label ? ` (${l.variant_label})` : ""} <span className="upr-eyebrow ml-1">× {l.quantity}</span>
                    </span>
                  ))}
                </div>
                <div className="text-sm" style={{ color: "var(--upr-muted)" }}>
                  {new Date(o.created_at).toLocaleString()} · session {o.session_id?.slice(0, 14)}…
                  {o.email ? <> · <a className="upr-link-underline" href={`mailto:${o.email}`}>{o.email}</a></> : null}
                </div>
                {(o.shipping_zone && o.shipping_zone !== "digital" && Number(o.shipping || 0) > 0) && (
                  <div className="text-xs mt-1" style={{ color: "var(--upr-muted)" }} data-testid={`order-${o.id}-shipping`}>
                    Ship to <strong>{o.shipping_country || "—"}</strong> · {o.shipping_label || o.shipping_zone.toUpperCase()} · {(o.currency || "").toUpperCase()} {Number(o.shipping).toFixed(2)}
                  </div>
                )}
                {o.shipping_zone === "digital" && (
                  <div className="text-xs mt-1" style={{ color: "var(--upr-muted)" }}>Digital only — no shipping</div>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="font-heading font-extrabold">{(o.currency || "").toUpperCase()} {Number(o.amount).toFixed(2)}</div>
                <div className={"upr-eyebrow " + (o.payment_status === "paid" ? "text-[color:var(--upr-secondary)]" : o.payment_status === "initiated" ? "text-[color:var(--upr-muted)]" : "text-[color:var(--upr-primary)]")}>
                  {o.payment_status}
                </div>
                {o.confirmation_sent_at && (
                  <div className="upr-eyebrow mt-1" style={{ fontSize: "0.55rem", color: "var(--upr-muted)" }} title={new Date(o.confirmation_sent_at).toLocaleString()}>
                    ✓ email sent
                  </div>
                )}
              </div>
            </div>
            {o.payment_status === "paid" && o.email && (
              <div className="mt-3 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  className="upr-btn upr-btn-ghost"
                  onClick={() => resendConfirmation(o)}
                  disabled={resendingConf === o.session_id}
                  data-testid={`order-${o.id}-resend-confirmation`}
                  title={o.confirmation_sent_at ? `Last sent ${new Date(o.confirmation_sent_at).toLocaleString()}` : "Send the full order confirmation email"}
                >
                  <Send size={12} /> {resendingConf === o.session_id ? "Sending…" : (o.confirmation_sent_at ? "Re-send confirmation" : "Send confirmation")}
                </button>
                <button
                  type="button"
                  className="upr-btn upr-btn-ghost"
                  onClick={() => resend(o)}
                  disabled={resending === o.session_id}
                  data-testid={`order-${o.id}-resend`}
                  title="Re-send only the bundled download link to the customer's email (extends expiry by 30 days)"
                >
                  <Send size={12} /> {resending === o.session_id ? "Sending…" : "Re-send download link"}
                </button>
              </div>
            )}
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                className="upr-btn upr-btn-ghost"
                onClick={() => onDelete(o)}
                disabled={deleting === o.session_id}
                data-testid={`order-${o.id}-delete`}
                title="Remove this order from the ledger — cannot be undone"
              >
                <Trash2 size={12} /> {deleting === o.session_id ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
          );
        })}
      </div>
    </Section>
  );
}

/* --------------- DISCOUNT CODES --------------- */
const EMPTY_DISCOUNT = { code: "", type: "percent", value: 10, max_uses: null, expires_at: "", active: true, description: "" };
function DiscountsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/discounts").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        ...editing,
        value: Number(editing.value) || 0,
        max_uses: editing.max_uses === "" || editing.max_uses == null ? null : Number(editing.max_uses),
      };
      if (editing.id) await api.put(`/discounts/${editing.id}`, payload);
      else await api.post("/discounts", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onDelete = async (it) => {
    if (!window.confirm(`Delete code "${it.code}"?`)) return;
    try { await api.delete(`/discounts/${it.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Section title="Codes" onAdd={() => { setEditing({ ...EMPTY_DISCOUNT }); setOpen(true); }}>
      <div data-testid="admin-discounts-list">
        {items.length === 0 && <div className="text-sm" style={{ color: "var(--upr-muted)" }}>No discount codes yet.</div>}
        {items.map((d) => {
          const limit = d.max_uses ? ` · ${d.used_count}/${d.max_uses} used` : ` · ${d.used_count} used`;
          const value = d.type === "percent" ? `${d.value}% off` : `EUR ${Number(d.value).toFixed(2)} off`;
          return (
            <ItemRow
              key={d.id}
              title={`${d.code} — ${value}`}
              subtitle={`${d.active ? "Active" : "Inactive"}${limit}${d.expires_at ? " · expires " + d.expires_at : ""}${d.description ? " · " + d.description : ""}`}
              onEdit={() => { setEditing({ ...d }); setOpen(true); }}
              onDelete={() => onDelete(d)}
              testid={`discount-row-${d.id}`}
            />
          );
        })}
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing?.id ? "Edit code" : "New code"}
        footer={editing && (
          <>
            <button className="upr-btn upr-btn-ghost" onClick={() => setOpen(false)} type="button">Cancel</button>
            <button className="upr-btn" onClick={save} type="button" data-testid="disc-save"><Save size={14} /> Save</button>
          </>
        )}
      >
        {editing && (
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Code (uppercase, no spaces)" value={editing.code} onChange={(v) => setEditing({ ...editing, code: v.toUpperCase().replace(/\s+/g, "") })} testid="disc-code" required />
            <label className="block">
              <span className="upr-eyebrow block mb-2">Type</span>
              <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })}
                data-testid="disc-type"
                className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]" style={{ borderColor: "var(--upr-border)" }}>
                <option value="percent">Percent (%)</option>
                <option value="fixed">Fixed amount</option>
              </select>
            </label>
            <Input label={editing.type === "percent" ? "Value (%)" : "Value (in item currency)"} type="number" value={editing.value} onChange={(v) => setEditing({ ...editing, value: v })} testid="disc-value" required />
            <Input label="Max uses (blank = unlimited)" type="number" value={editing.max_uses ?? ""} onChange={(v) => setEditing({ ...editing, max_uses: v === "" ? null : v })} testid="disc-max" />
            <Input label="Expires at (YYYY-MM-DD or ISO datetime, blank = never)" value={editing.expires_at || ""} onChange={(v) => setEditing({ ...editing, expires_at: v })} testid="disc-expires" />
            <Checkbox label="Active" value={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} testid="disc-active" />
            <div className="md:col-span-2"><Input label="Description (shown to customer)" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testid="disc-desc" /></div>
          </div>
        )}
      </Modal>
    </Section>
  );
}

/* --------------- SUBSCRIBERS --------------- */
function SubscribersAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/newsletter/subscribers").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const onDelete = async (it) => {
    if (!window.confirm(`Unsubscribe ${it.email}?`)) return;
    try { await api.delete(`/newsletter/subscribers/${it.id}`); toast.success("Unsubscribed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Section title="Subscribers">
      <div data-testid="admin-subscribers-list">
        {items.length === 0 && <div className="text-sm" style={{ color: "var(--upr-muted)" }}>No subscribers yet.</div>}
        {items.length > 0 && (
          <div className="upr-eyebrow mb-4">{items.length} active subscriber{items.length === 1 ? "" : "s"}</div>
        )}
        {items.map((s) => (
          <div key={s.id} className="flex items-center justify-between py-3 border-b" style={{ borderColor: "var(--upr-border)" }} data-testid={`subscriber-${s.id}`}>
            <div>
              <div className="font-heading font-extrabold">{s.email}</div>
              <div className="text-xs" style={{ color: "var(--upr-muted)" }}>
                {s.source} · {new Date(s.created_at).toLocaleDateString()}
              </div>
            </div>
            <button className="upr-btn upr-btn-ghost" onClick={() => onDelete(s)} data-testid={`subscriber-${s.id}-delete`}><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* --------------- MESSAGES --------------- */
function MessagesAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/contact").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const onDelete = async (it) => {
    if (!window.confirm("Delete this message?")) return;
    try { await api.delete(`/contact/${it.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onRead = async (it) => {
    try { await api.put(`/contact/${it.id}/read`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Section title="Messages">
      <div data-testid="admin-messages-list">
        {items.length === 0 && <div className="text-sm" style={{ color: "var(--upr-muted)" }}>No messages yet.</div>}
        {items.map((m) => (
          <div key={m.id} className="border p-4 mb-3" style={{ borderColor: "var(--upr-border)", background: m.read ? "transparent" : "var(--upr-surface)" }} data-testid={`msg-${m.id}`}>
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <div className="font-heading font-extrabold">{m.subject || "(no subject)"}</div>
                <div className="text-sm" style={{ color: "var(--upr-muted)" }}>
                  {m.name} · <a className="upr-link-underline" href={`mailto:${m.email}`}>{m.email}</a> · {new Date(m.created_at).toLocaleString()}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                {!m.read && <button className="upr-btn upr-btn-ghost" onClick={() => onRead(m)} data-testid={`msg-${m.id}-read`}>Mark read</button>}
                <button className="upr-btn upr-btn-ghost" onClick={() => onDelete(m)} data-testid={`msg-${m.id}-delete`}><Trash2 size={12} /></button>
              </div>
            </div>
            <p className="mt-3 text-sm whitespace-pre-wrap">{m.message}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

